import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Unlock, ShieldAlert, User, ScrollText, Ticket, Ban, RefreshCw, MessageSquareText, Star } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "lookup", label: "Student Lookup", icon: User },
  { id: "locked", label: "Locked Accounts", icon: ShieldAlert },
  { id: "events", label: "Event Log", icon: ScrollText },
  { id: "flashcards", label: "Flashcard Codes", icon: Ticket },
  { id: "feedback", label: "Question Feedback", icon: MessageSquareText },
  { id: "reviews", label: "Reviews", icon: Star },
];

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("lookup");

  useEffect(() => {
    if (user && user !== false && !user.is_admin) navigate("/", { replace: true });
  }, [user, navigate]);

  if (!user || user === false || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button data-testid="admin-back-btn" onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Admin · Support</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-2 mb-8 flex-wrap items-center">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border transition-colors ${
                tab === t.id ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
          <div className="ml-auto"><ReconcileButton /></div>
        </div>
        {tab === "lookup" && <LookupTab />}
        {tab === "locked" && <LockedTab />}
        {tab === "events" && <EventsTab />}
        {tab === "flashcards" && <FlashcardsTab />}
        {tab === "feedback" && <FeedbackTab />}
        {tab === "reviews" && <ReviewsTab />}
      </main>
    </div>
  );
}

const ReconcileButton = () => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Run enrollment reconciliation now? Students unenrolled in Thinkific will be revoked immediately (instead of waiting for the daily 7am sweep).")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reconcile");
      if (data.skipped) toast.info(`Skipped: ${data.reason}`);
      else if (data.error) toast.error(`Reconciliation failed: ${data.error} — alert emailed`);
      else if (data.guard_tripped) toast.warning(`Revoke guard tripped (${data.to_revoke.length} pending) — NOTHING revoked. See the alert email / RUNBOOK bulk-unenrollment procedure.`);
      else toast.success(`Done — ${data.enrolled} enrolled · ${data.missing.length} missing provisioning · ${data.revoked} revoked · ${data.reinstated} reinstated · ${data.synced} access dates synced`);
    } catch (e) {
      toast.error(apiError(e, "Reconciliation failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid="admin-reconcile-btn" onClick={run} disabled={busy}
      className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors">
      <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Running…" : "Run reconciliation now"}
    </button>
  );
};

const StatusChip = ({ status, active }) => (
  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${
    status === "locked" ? "bg-rose-50 text-rose-700 border-rose-200"
    : status === "revoked" ? "bg-slate-100 text-slate-600 border-slate-300"
    : active ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-amber-50 text-amber-700 border-amber-200"}`}>
    {status === "locked" ? "Locked" : status === "revoked" ? "Revoked" : active ? "Active" : "Access ended"}
  </span>
);

const LookupTab = () => {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/users", { params: { email: email.trim() } });
      setResult(data);
    } catch (err) {
      toast.error(apiError(err, "Lookup failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <form onSubmit={search} className="flex gap-2 max-w-lg mb-6">
        <input data-testid="admin-lookup-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="student@example.com"
          className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <button data-testid="admin-lookup-btn" type="submit" disabled={busy}
          className="flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          <Search className="w-4 h-4" /> Look up
        </button>
      </form>
      {result && (
        <div data-testid="admin-lookup-result" className="bg-white rounded-xl border border-slate-200 p-6 max-w-3xl">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="font-semibold text-slate-900">{result.email}</div>
              <div className="text-xs text-slate-400">{result.name || "No name on file"} · Thinkific ID: {result.thinkific_user_id ?? "—"}</div>
            </div>
            <div className="flex items-center gap-2">
              <StatusChip status={result.status} active={result.access_active} />
              {result.status !== "revoked" && (
                <button
                  data-testid="admin-revoke-btn"
                  onClick={async () => {
                    if (!window.confirm(`Revoke exam access for ${result.email}? They will see the access-ended state. A new qualifying order re-provisions automatically.`)) return;
                    try {
                      await api.post(`/admin/users/${result.id}/revoke`);
                      toast.success("Access revoked");
                      search();
                    } catch (err) {
                      toast.error(apiError(err, "Revoke failed"));
                    }
                  }}
                  className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors"
                >
                  <Ban className="w-3.5 h-3.5" /> Revoke access
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-5">
            <div><div className="text-xs text-slate-400">Access start</div><div className="text-slate-800">{fmt(result.access_start)}</div></div>
            <div><div className="text-xs text-slate-400">Access end</div><div className="text-slate-800">{fmt(result.access_end)}</div></div>
            <div><div className="text-xs text-slate-400">Locked until</div><div className="text-slate-800">{fmt(result.locked_until)}</div></div>
            <div><div className="text-xs text-slate-400">Completed attempts</div><div className="text-slate-800">{result.completed_attempts}</div></div>
          </div>
          <div className="mb-5 text-sm">
            <div className="text-xs text-slate-400">Brainscape code</div>
            <div data-testid="admin-flashcard-code" className="font-mono text-slate-800">
              {result.flashcard_code ? `${result.flashcard_code} (assigned ${fmt(result.flashcard_code_assigned_at)})` : "Not requested yet"}
            </div>
          </div>
          <div className="mb-5 text-sm">
            <div className="text-xs text-slate-400">45-day review/referral email</div>
            <div className="flex items-center gap-3 mt-0.5">
              <span data-testid="admin-optout-state" className="text-slate-800">
                {result.marketing_opt_out ? "Opted out — will never receive it" : "Eligible (sent once, 45 days after provisioning)"}
              </span>
              <button
                data-testid="admin-optout-toggle"
                onClick={async () => {
                  try {
                    await api.post(`/admin/users/${result.id}/marketing-opt-out`, { opt_out: !result.marketing_opt_out });
                    toast.success(result.marketing_opt_out ? "Opt-out removed" : "Opted out of the 45-day email");
                    search();
                  } catch (err) {
                    toast.error(apiError(err, "Update failed"));
                  }
                }}
                className="text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                {result.marketing_opt_out ? "Remove opt-out" : "Opt out"}
              </button>
            </div>
          </div>
          {result.guarantee && (
            <div data-testid="admin-guarantee" className="mb-5">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">
                Pass guarantee — best Testing Mode score per exam
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                  result.guarantee.qualified ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                  {result.guarantee.qualified ? "Qualifies" : "Not yet qualified"}
                </span>
                <span data-testid="admin-guarantee-count" className="text-sm text-slate-700">
                  {result.guarantee.exams_qualified} of {result.guarantee.required_exams} exams at ≥{result.guarantee.threshold_pct}%
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {result.guarantee.per_exam.map((e) => (
                  <span key={e.exam_number} title={`Exam ${e.exam_number} best: ${e.best_pct ?? "—"}%`}
                    className={`font-mono text-[11px] px-2 py-0.5 rounded border ${
                      e.qualifies ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : e.best_pct != null ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                    E{e.exam_number}: {e.best_pct != null ? `${e.best_pct}%` : "—"}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">
            Active sessions ({result.active_sessions.length}/3)
          </div>
          {result.active_sessions.length === 0 ? (
            <p className="text-sm text-slate-400">No active sessions.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="py-1.5 font-medium">Signed in</th><th className="font-medium">Last seen</th><th className="font-medium">IP</th><th className="font-medium">Device</th>
              </tr></thead>
              <tbody>
                {result.active_sessions.map((s) => (
                  <tr key={s.id} className="border-b border-slate-50 text-slate-700">
                    <td className="py-1.5">{fmt(s.created_at)}</td><td>{fmt(s.last_seen_at)}</td>
                    <td className="font-mono text-xs">{s.ip}</td><td className="font-mono text-xs">{s.ua_hash}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const LockedTab = () => {
  const [rows, setRows] = useState(null);

  const load = () =>
    api.get("/admin/locked").then(({ data }) => setRows(data.locked)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const unlock = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unlock`);
      toast.success(`${u.email} unlocked`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Unlock failed"));
    }
  };

  if (!rows) return <p className="text-sm text-slate-400">Loading…</p>;
  if (rows.length === 0) return <p data-testid="admin-no-locked" className="text-sm text-slate-400">No locked accounts. Enforcement is doing its job quietly.</p>;
  return (
    <div data-testid="admin-locked-list" className="bg-white rounded-xl border border-slate-200 max-w-3xl divide-y divide-slate-100">
      {rows.map((u) => (
        <div key={u.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-medium text-slate-900">{u.email}</div>
            <div className="text-xs text-slate-400">Locked until {fmt(u.locked_until)} · access ends {fmt(u.access_end)}</div>
          </div>
          <button data-testid={`admin-unlock-${u.email}`} onClick={() => unlock(u)}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors">
            <Unlock className="w-4 h-4" /> Unlock
          </button>
        </div>
      ))}
    </div>
  );
};

const FlashcardsTab = () => {
  const [stats, setStats] = useState(null);
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/flashcard-codes/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const doImport = async (e) => {
    e.preventDefault();
    if (!csv.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/flashcard-codes/import", { csv });
      toast.success(`Imported ${data.inserted} codes (${data.duplicates} duplicates skipped)`);
      setCsv("");
      load();
    } catch (err) {
      toast.error(apiError(err, "Import failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      {stats && (
        <div data-testid="flashcard-stats" className="flex gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 px-5 py-4">
            <div className={`font-mono text-2xl font-semibold ${stats.unassigned <= stats.low_threshold ? "text-rose-600" : "text-slate-900"}`}>
              {stats.unassigned}
            </div>
            <div className="text-xs text-slate-400">unassigned remaining {stats.unassigned <= stats.low_threshold && "· LOW"}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 px-5 py-4">
            <div className="font-mono text-2xl font-semibold text-slate-900">{stats.assigned}</div>
            <div className="text-xs text-slate-400">assigned to students</div>
          </div>
        </div>
      )}
      <form onSubmit={doImport}>
        <label className="block text-sm font-medium text-slate-700 mb-2">Bulk import (CSV — one code per line, first column used)</label>
        <textarea
          data-testid="flashcard-import-textarea"
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={8}
          placeholder={"code\nBSC-AAAA-0001\nBSC-AAAA-0002"}
          className="w-full font-mono text-sm px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-600"
        />
        <button data-testid="flashcard-import-btn" type="submit" disabled={busy || !csv.trim()}
          className="mt-3 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors">
          {busy ? "Importing…" : "Import codes"}
        </button>
      </form>
      <p className="mt-3 text-xs text-slate-400 max-w-lg leading-relaxed">
        Brainscape bills on redemption — codes are only handed out when a student requests one, never at
        provisioning. A low-inventory alert emails automatically below the threshold.
      </p>
    </div>
  );
};

const EventsTab = () => {
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState(null);

  const load = (e) => {
    e?.preventDefault();
    api.get("/admin/events", { params: { limit: 100, ...(email.trim() ? { email: email.trim() } : {}) } })
      .then(({ data }) => setRows(data.events))
      .catch((err) => toast.error(apiError(err)));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <form onSubmit={load} className="flex gap-2 max-w-lg mb-5">
        <input data-testid="admin-events-email-input" type="text" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Filter by student email (optional)"
          className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <button data-testid="admin-events-filter-btn" type="submit"
          className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">Filter</button>
      </form>
      {!rows ? <p className="text-sm text-slate-400">Loading…</p> : rows.length === 0 ? (
        <p className="text-sm text-slate-400">No provisioning events match.</p>
      ) : (
        <div data-testid="admin-events-list" className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2 font-medium">When</th><th className="px-2 font-medium">Event</th>
              <th className="px-2 font-medium">Email</th><th className="px-2 font-medium">Details</th>
            </tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-b border-slate-50 text-slate-700 align-top">
                  <td className="px-4 py-2 whitespace-nowrap text-xs">{fmt(e.timestamp)}</td>
                  <td className="px-2 py-2"><span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{e.event_type}</span></td>
                  <td className="px-2 py-2 text-xs">{e.email || "—"}</td>
                  <td className="px-2 py-2 text-xs text-slate-500 font-mono max-w-md truncate">
                    {JSON.stringify(Object.fromEntries(Object.entries(e.details).filter(([k]) => k !== "email")))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


const FeedbackTab = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/feedback")
      .then(({ data }) => setRows(data.feedback))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  if (!rows) return <p className="text-sm text-slate-400">Loading…</p>;
  if (rows.length === 0) return <p data-testid="admin-feedback-empty" className="text-sm text-slate-400">No question feedback yet.</p>;
  return (
    <div data-testid="admin-feedback-list" className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
          <th className="px-4 py-2 font-medium">When</th><th className="px-2 font-medium">Student</th>
          <th className="px-2 font-medium">Exam</th><th className="px-2 font-medium">Question</th>
          <th className="px-2 font-medium">Note</th>
        </tr></thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id} className="border-b border-slate-50 text-slate-700 align-top">
              <td className="px-4 py-2 whitespace-nowrap text-xs">{fmt(f.created_at)}</td>
              <td className="px-2 py-2 text-xs">{f.email}</td>
              <td className="px-2 py-2 text-xs font-mono">{f.exam_number}</td>
              <td className="px-2 py-2 text-xs font-mono">Q{f.question_number}</td>
              <td className="px-2 py-2 text-xs text-slate-600 max-w-lg">{f.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RatingStars = ({ n }) => (
  <span className="inline-flex items-center gap-0.5" title={`${n} of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
    ))}
  </span>
);

const ReviewsTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/reviews")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  if (!data) return <p className="text-sm text-slate-400">Loading…</p>;
  const { summary, reviews } = data;
  return (
    <div>
      <div data-testid="admin-reviews-summary" className="mb-5 flex items-center gap-4 bg-white rounded-xl border border-slate-200 px-5 py-4 max-w-md">
        <div>
          <div className="font-mono text-2xl font-semibold text-slate-900">{summary.average ?? "—"}</div>
          <div className="text-[11px] text-slate-400">average rating</div>
        </div>
        {summary.average != null && <RatingStars n={Math.round(summary.average)} />}
        <div className="ml-auto text-right">
          <div className="font-mono text-2xl font-semibold text-slate-900">{summary.count}</div>
          <div className="text-[11px] text-slate-400">review{summary.count === 1 ? "" : "s"}</div>
        </div>
      </div>
      {reviews.length === 0 ? (
        <p data-testid="admin-reviews-empty" className="text-sm text-slate-400">No reviews yet.</p>
      ) : (
        <div data-testid="admin-reviews-list" className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2 font-medium">Updated</th><th className="px-2 font-medium">Student</th>
              <th className="px-2 font-medium">Rating</th><th className="px-2 font-medium">Comment</th>
            </tr></thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 text-slate-700 align-top">
                  <td className="px-4 py-2 whitespace-nowrap text-xs">{fmt(r.updated_at)}</td>
                  <td className="px-2 py-2 text-xs">{r.email}</td>
                  <td className="px-2 py-2"><RatingStars n={r.rating} /></td>
                  <td className="px-2 py-2 text-xs text-slate-600 max-w-lg">{r.comment || <span className="text-slate-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
